import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { adminGetLocations, adminCreateLocation, adminUpdateLocation, adminDeleteLocation } from "@/lib/api";

export default function LocationsAdmin() {
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ name: "", address: "" });

  const load = () => adminGetLocations().then(setLocations).catch(() => toast.error("Failed to load locations"));
  useEffect(load, []);

  const add = async () => {
    if (!form.name.trim()) return;
    try {
      await adminCreateLocation({ name: form.name.trim(), address: form.address.trim() });
      setForm({ name: "", address: "" });
      toast.success("Location added");
      load();
    } catch { toast.error("Failed to add location"); }
  };

  const toggleActive = async (l) => {
    await adminUpdateLocation(l.id, { name: l.name, address: l.address, active: !l.active }).catch(() => toast.error("Update failed"));
    load();
  };

  const remove = async (l) => {
    if (!window.confirm(`Remove "${l.name}"?`)) return;
    await adminDeleteLocation(l.id).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div data-testid="admin-locations">
      <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Pickup Locations</h1>
      <p className="text-[#1F3864]/60 mb-8">Where customers can choose to pick up their orders at checkout.</p>

      <div className="bg-white rounded-2xl border border-[#1F3864]/10 p-5 mb-6 flex flex-wrap gap-3">
        <input data-testid="location-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Location name (e.g. Rada Studio Manila)"
          className="flex-1 min-w-[200px] border border-[#1F3864]/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#1F3864]" />
        <input data-testid="location-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
          placeholder="Address"
          className="flex-1 min-w-[200px] border border-[#1F3864]/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#1F3864]" />
        <button data-testid="add-location-btn" onClick={add}
          className="inline-flex items-center gap-2 bg-[#1F3864] text-[#D7E85C] font-bold px-4 py-2.5 rounded-full">
          <Plus size={16} /> Add
        </button>
      </div>

      <div className="space-y-2">
        {locations.map((l) => (
          <div key={l.id} data-testid={`location-row-${l.id}`}
            className="bg-white border border-[#1F3864]/10 rounded-xl px-4 py-3.5 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[180px]">
              <div className="font-bold text-[#1F3864]">{l.name}</div>
              <div className="text-xs text-[#1F3864]/50">{l.address}</div>
            </div>
            <button data-testid={`toggle-location-${l.id}`} onClick={() => toggleActive(l)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full ${l.active ? "bg-[#D7E85C]/30 text-[#5C6B12]" : "bg-[#1F3864]/10 text-[#1F3864]/50"}`}>
              {l.active ? "Active" : "Hidden"}
            </button>
            <button onClick={() => remove(l)} className="text-[#FF8473]/70 hover:text-[#FF8473]"><X size={16} /></button>
          </div>
        ))}
        {locations.length === 0 && <p className="text-center text-[#1F3864]/50 text-sm py-8" data-testid="locations-empty">No locations yet.</p>}
      </div>
    </div>
  );
}
